/**
 * Productos en el shape ProductDTO que espera la UI:
 *   - translations[locale, name, description]
 *   - prices[currency, amount (en centavos), compareAtAmount]
 *   - variants con attributes Record<string, string>
 *   - inventory inline (luego también vive en seeds/inventory.ts → mismas IDs)
 */

interface Variant {
  id: string
  sku: string | null
  barcode: string | null
  status: 'draft' | 'published' | 'archived'
  attributes: Record<string, string>
  images: string[]
  prices: Array<{ currency: string; amount: number; compareAtAmount: number | null }>
  inventory: { quantity: number; reservedQuantity: number; lowStockThreshold: number } | null
}

interface Product {
  id: string
  slug: string
  brand: string | null
  sku: string | null
  status: 'draft' | 'published' | 'archived'
  images: string[]
  translations: Array<{ locale: string; name: string; description: string | null }>
  prices: Array<{ currency: string; amount: number; compareAtAmount: number | null }>
  reviewCount: number
  averageRating: number
  likeCount: number
  categories: Array<{ id: string; name: string }>
  variants: Variant[]
  inventory: { quantity: number; reservedQuantity: number; lowStockThreshold: number } | null
  createdAt: string
  updatedAt: string
}

const COP = (cop: number) => cop * 100 // unidad mínima

function v(id: string, sku: string, size: string, priceCop: number, stock: number, reserved = 0): Variant {
  return {
    id, sku, barcode: null, status: 'published',
    attributes: { size },
    images: [],
    prices: [{ currency: 'COP', amount: COP(priceCop), compareAtAmount: null }],
    inventory: { quantity: stock, reservedQuantity: reserved, lowStockThreshold: 3 },
  }
}

export const PRODUCTS_DEMO: Product[] = [
  {
    id: 'prd_01', slug: 'hoodie-la-manzana-de-eva', brand: 'Centrio Studios', sku: 'EUTANASIA_01',
    status: 'published',
    images: ['https://picsum.photos/seed/h1/800', 'https://picsum.photos/seed/h1b/800'],
    translations: [{ locale: 'es-CO', name: 'Hoodie "La Manzana de Eva"', description: 'Hoodie oversize. 100% algodón premium.\n\nGramaje 320 g/m². Fit boxy.' }],
    prices: [{ currency: 'COP', amount: COP(295_000), compareAtAmount: COP(350_000) }],
    reviewCount: 14, averageRating: 4.7, likeCount: 86,
    categories: [{ id: 'cat_01', name: 'Hoodies' }],
    variants: [
      v('var_01a', 'EUTANASIA_01-M',  'M',  295_000, 12, 1),
      v('var_01b', 'EUTANASIA_01-L',  'L',  295_000, 8),
      v('var_01c', 'EUTANASIA_01-XL', 'XL', 295_000, 3),
    ],
    inventory: null,
    createdAt: '2026-02-01T10:00:00.000Z', updatedAt: '2026-04-12T08:30:00.000Z',
  },
  {
    id: 'prd_02', slug: 'long-sleeve-somnolencia', brand: 'Centrio Studios', sku: 'EUTANASIA_02',
    status: 'published',
    images: ['https://picsum.photos/seed/ls2/800'],
    translations: [{ locale: 'es-CO', name: 'Long Sleeve "Somnolencia"', description: 'Una pieza que representa ese estado entre la vigilia y el sueño\n\n100% algodón premium\nGramaje: 260 g/m²\nFit Boxyfit/oversized' }],
    prices: [{ currency: 'COP', amount: COP(240_000), compareAtAmount: null }],
    reviewCount: 22, averageRating: 4.5, likeCount: 142,
    categories: [{ id: 'cat_02', name: 'Camisas' }],
    variants: [
      v('var_02a', 'EUTANASIA_02-S',   'S',   240_000, 14),
      v('var_02b', 'EUTANASIA_02-M',   'M',   240_000, 18, 2),
      v('var_02c', 'EUTANASIA_02-L',   'L',   240_000, 10),
      v('var_02d', 'EUTANASIA_02-XL',  'XL',  240_000, 6),
      v('var_02e', 'EUTANASIA_02-XXL', 'XXL', 240_000, 6, 2),
    ],
    inventory: null,
    createdAt: '2026-02-05T11:00:00.000Z', updatedAt: '2026-04-22T09:00:00.000Z',
  },
  {
    id: 'prd_03', slug: 'tracksuit-osmosis', brand: 'Centrio Studios', sku: 'EUTANASIA_03',
    status: 'published',
    images: ['https://picsum.photos/seed/ts3/800'],
    translations: [{ locale: 'es-CO', name: 'Tracksuit "Ósmosis"', description: 'Conjunto sudadera + pantalón. Algodón francés. Doble pespunte.' }],
    prices: [{ currency: 'COP', amount: COP(430_000), compareAtAmount: COP(480_000) }],
    reviewCount: 8, averageRating: 4.9, likeCount: 67,
    categories: [{ id: 'cat_03', name: 'Tracksuits' }],
    variants: [
      v('var_03a', 'EUTANASIA_03-S', 'S', 430_000, 9, 1),
      v('var_03b', 'EUTANASIA_03-M', 'M', 430_000, 20, 1),
      v('var_03c', 'EUTANASIA_03-L', 'L', 430_000, 7),
    ],
    inventory: null,
    createdAt: '2026-02-12T09:30:00.000Z', updatedAt: '2026-04-30T16:00:00.000Z',
  },
  {
    id: 'prd_04', slug: 'camiseta-anestesia', brand: 'Centrio Studios', sku: 'EUTANASIA_04',
    status: 'published',
    images: ['https://picsum.photos/seed/t4/800'],
    translations: [{ locale: 'es-CO', name: 'Camiseta "Anestesia"', description: 'Camiseta gráfica. 100% algodón. Fit regular.' }],
    prices: [{ currency: 'COP', amount: COP(195_000), compareAtAmount: null }],
    reviewCount: 3, averageRating: 4.0, likeCount: 18,
    categories: [{ id: 'cat_02', name: 'Camisas' }],
    variants: [
      v('var_04a', 'EUTANASIA_04-M', 'M', 195_000, 0),
      v('var_04b', 'EUTANASIA_04-L', 'L', 195_000, 1),
    ],
    inventory: null,
    createdAt: '2026-03-01T12:00:00.000Z', updatedAt: '2026-04-15T10:00:00.000Z',
  },
  {
    id: 'prd_05', slug: 'hoodie-hora-final', brand: 'Centrio Studios', sku: 'EUTANASIA_05',
    status: 'draft',
    images: ['https://picsum.photos/seed/h5/800'],
    translations: [{ locale: 'es-CO', name: 'Hoodie "Hora Final"', description: 'Hoodie negro con estampado frontal. (Borrador, aún no publicado.)' }],
    prices: [{ currency: 'COP', amount: COP(275_000), compareAtAmount: null }],
    reviewCount: 0, averageRating: 0, likeCount: 0,
    categories: [{ id: 'cat_01', name: 'Hoodies' }],
    variants: [
      v('var_05a', 'EUTANASIA_05-M', 'M', 275_000, 0),
      v('var_05b', 'EUTANASIA_05-L', 'L', 275_000, 0),
    ],
    inventory: null,
    createdAt: '2026-04-20T14:00:00.000Z', updatedAt: '2026-04-20T14:00:00.000Z',
  },
  {
    id: 'prd_06', slug: 'cap-ultima-cena', brand: 'Centrio Studios', sku: 'EUTANASIA_06',
    status: 'published',
    images: ['https://picsum.photos/seed/c6/800'],
    translations: [{ locale: 'es-CO', name: 'Cap "Última Cena"', description: 'Gorra dad-cap bordada. Talla única.' }],
    prices: [{ currency: 'COP', amount: COP(95_000), compareAtAmount: null }],
    reviewCount: 11, averageRating: 4.8, likeCount: 54,
    categories: [{ id: 'cat_04', name: 'Accesorios' }],
    variants: [],
    inventory: { quantity: 25, reservedQuantity: 0, lowStockThreshold: 5 },
    createdAt: '2026-03-15T10:00:00.000Z', updatedAt: '2026-04-01T11:00:00.000Z',
  },
]
